import { ErrorAlert } from '@if/mui'
import AddIcon from '@mui/icons-material/Add'
import { Box, CircularProgress, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import { useComputeCalendarAction } from '@src/api/loan/loan-api'
import FormDialog from '@src/components/Common/Dialog/FormDialog'
import { useUnmountableDialog } from '@src/services/dialog'
import { ECalendarSimulationAction, SignedCalendarSimulation } from '@src/types/loan/loan-simulation'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LoanCalendarActionPreviewSummaryCompact } from '../LoanCalendarActionPreview/LoanCalendarActionPreviewSummary'
import { AddFeeSchemaType } from './AddFee-schema'
import AddFeeDialog from './AddFeeDialog'
import FeeTable from './FeeTable'

interface FeesDialogProps {
  initialSignedSimulation: SignedCalendarSimulation
  onConfirm: (confirmedSimulation: SignedCalendarSimulation) => void
  onClose: () => void
  isLockedByOther?: boolean
}

const FeesDialog = ({ initialSignedSimulation, onConfirm, onClose, isLockedByOther }: FeesDialogProps) => {
  const { t } = useTranslation()

  const [openAddFeeDialog, setOpenAddFeeDialog] = useState(false)
  const [hasChanges, setHasChanges] = useState(false)

  const [currentSimulation, setCurrentSimulation] = useState<SignedCalendarSimulation>(initialSignedSimulation)

  const {
    mutateAsync: computeAction,
    error,
    isPending,
  } = useComputeCalendarAction(
    initialSignedSimulation.simulation.fullLoan.loan.id,
    initialSignedSimulation.simulation.fullLoan.loan.financingProgramId,
  )

  const existingFees = useMemo(() => {
    return currentSimulation.simulation.getExistingFees()
  }, [currentSimulation.simulation])

  const { dialogProps, closeDialog } = useUnmountableDialog(onClose, isPending)

  const handleOnConfirmAddFee = async (fee: AddFeeSchemaType) => {
    setOpenAddFeeDialog(false)
    const result = await computeAction({
      ...currentSimulation,
      action: {
        actionType: ECalendarSimulationAction.AddFee,
        effectiveDate: fee.effectiveDate,
        amount: fee.amount,
        feeType: fee.feeType,
        note: fee.note || undefined,
      },
    })
    setCurrentSimulation(result)
    setHasChanges(true)
  }

  const handleConfirm = () => {
    onConfirm(currentSimulation)
    closeDialog()
  }

  return (
    <>
      <FormDialog
        sx={{
          '& .MuiDialog-paper': {
            minHeight: '50vh',
            maxHeight: '50vh',
          },
        }}
        {...dialogProps}
        title={
          <Stack spacing={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Typography variant="inherit">{t('loan.feesDialog.title')}</Typography>
              <Box>
                <LoanCalendarActionPreviewSummaryCompact
                  simulationBefore={initialSignedSimulation.simulation}
                  simulationAfter={currentSimulation.simulation}
                  actionDate={initialSignedSimulation.simulation.fullLoan.loan.lastPaymentOn}
                />
              </Box>
            </Stack>
            {error && <ErrorAlert error={error} disabledGutterBottom />}
          </Stack>
        }
        onConfirm={handleConfirm}
        dividers
        disableGutterContent
        disabled={isPending || isLockedByOther || !hasChanges}
        loading={isPending}
        confirmColor="primary"
        disableContentPaddingTop
        maxWidth="md"
        fullWidth
        additionalActions={
          !isLockedByOther && (
            <Box sx={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Tooltip title={t('loan.feesDialog.addFee')}>
                <IconButton color="primary" onClick={() => setOpenAddFeeDialog(true)} disabled={isPending}>
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </Box>
          )
        }
      >
        {isPending ? (
          <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress />
          </div>
        ) : (
          <FeeTable fees={existingFees} />
        )}
      </FormDialog>
      {openAddFeeDialog && (
        <AddFeeDialog
          isOpen={openAddFeeDialog}
          onClose={() => setOpenAddFeeDialog(false)}
          onConfirm={handleOnConfirmAddFee}
          operationInformations={currentSimulation.simulation.operationInformations}
        />
      )}
    </>
  )
}

export default FeesDialog
